import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ICE_COLORS } from './CyanMaterial';

// Icicle decoration at top of border
const Icicles = ({ width }) => {
  const icicles = useMemo(() => {
    const data = [];
    const count = 12;
    for (let i = 0; i < count; i++) {
      const x = (i / (count - 1)) * width - width / 2;
      data.push({
        x,
        height: 0.3 + Math.random() * 0.4,
        width: 0.08 + Math.random() * 0.05,
      });
    }
    return data;
  }, [width]);

  return (
    <group position={[0, 20.3, 0.15]}>
      {icicles.map((icicle, i) => (
        <mesh key={i} position={[icicle.x, 0, 0]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[icicle.width, icicle.height, 4]} />
          <meshStandardMaterial
            color={ICE_COLORS.icicle}
            emissive={ICE_COLORS.frost}
            emissiveIntensity={0.3}
            metalness={0.8}
            roughness={0.15}
            transparent
            opacity={0.8}
          />
        </mesh>
      ))}
    </group>
  );
};

// Frost particle floating effect in background
const FrostParticles = () => {
  const particlesRef = useRef();

  const particles = useMemo(() => {
    const data = [];
    for (let i = 0; i < 20; i++) {
      data.push({
        x: (Math.random() - 0.5) * 10,
        y: Math.random() * 20,
        z: -0.15 + Math.random() * 0.1,
        speed: 0.2 + Math.random() * 0.3,
        size: 0.03 + Math.random() * 0.03,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return data;
  }, []);

  useFrame((state) => {
    if (!particlesRef.current) return;
    const time = state.clock.elapsedTime;

    particlesRef.current.children.forEach((particle, i) => {
      const data = particles[i];
      // Gentle floating motion
      particle.position.y = (data.y + time * data.speed * 0.5) % 20;
      particle.position.x = data.x + Math.sin(time * 0.5 + data.phase) * 0.3;
      particle.material.opacity = 0.3 + Math.sin(time * 2 + data.phase) * 0.15;
    });
  });

  return (
    <group ref={particlesRef}>
      {particles.map((data, i) => (
        <mesh key={i} position={[data.x, data.y, data.z]}>
          <circleGeometry args={[data.size, 6]} />
          <meshBasicMaterial
            color={ICE_COLORS.snow}
            transparent
            opacity={0.4}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

const IceBoardGrid3D = ({
  rows = 20,
  columns = 10,
  cellSize = 1,
}) => {
  const halfWidth = (columns * cellSize) / 2;
  const height = rows * cellSize;
  const borderRadius = 0.25;
  const borderThickness = 0.15;
  const borderDepth = 0.25;

  const gridLines = useMemo(() => {
    const points = [];

    // Vertical lines
    for (let i = 0; i <= columns; i++) {
      const x = i * cellSize - halfWidth;
      points.push(new THREE.Vector3(x, 0, -0.1));
      points.push(new THREE.Vector3(x, height, -0.1));
    }

    // Horizontal lines
    for (let i = 0; i <= rows; i++) {
      const y = i * cellSize;
      points.push(new THREE.Vector3(-halfWidth, y, -0.1));
      points.push(new THREE.Vector3(halfWidth, y, -0.1));
    }

    return points;
  }, [rows, columns, cellSize, halfWidth, height]);

  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(gridLines);
  }, [gridLines]);

  // Create rounded rectangle border shape
  const borderShape = useMemo(() => {
    const shape = new THREE.Shape();
    const w = columns * cellSize + borderThickness * 2;
    const h = rows * cellSize + borderThickness * 2;
    const r = borderRadius;

    // Outer rounded rectangle
    shape.moveTo(-w / 2 + r, -h / 2);
    shape.lineTo(w / 2 - r, -h / 2);
    shape.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + r);
    shape.lineTo(w / 2, h / 2 - r);
    shape.quadraticCurveTo(w / 2, h / 2, w / 2 - r, h / 2);
    shape.lineTo(-w / 2 + r, h / 2);
    shape.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - r);
    shape.lineTo(-w / 2, -h / 2 + r);
    shape.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + r, -h / 2);

    // Inner cutout
    const innerW = columns * cellSize;
    const innerH = rows * cellSize;
    const hole = new THREE.Path();
    hole.moveTo(-innerW / 2, -innerH / 2);
    hole.lineTo(innerW / 2, -innerH / 2);
    hole.lineTo(innerW / 2, innerH / 2);
    hole.lineTo(-innerW / 2, innerH / 2);
    hole.lineTo(-innerW / 2, -innerH / 2);
    shape.holes.push(hole);

    return shape;
  }, [rows, columns, cellSize, borderRadius, borderThickness]);

  return (
    <group>
      {/* Grid lines - subtle ice blue */}
      <lineSegments geometry={geometry}>
        <lineBasicMaterial
          color={ICE_COLORS.gridLine}
          opacity={0.2}
          transparent
        />
      </lineSegments>

      {/* Back panel - deep frozen background */}
      <mesh position={[0, rows * cellSize / 2, -0.2]} receiveShadow>
        <planeGeometry args={[columns * cellSize, rows * cellSize]} />
        <meshStandardMaterial
          color={ICE_COLORS.background}
          opacity={0.95}
          transparent
        />
      </mesh>

      {/* Frost particles in background */}
      <FrostParticles />

      {/* Border frame - frosted glass effect */}
      <mesh position={[0, height / 2, 0.05]}>
        <extrudeGeometry
          args={[
            borderShape,
            {
              depth: borderDepth,
              bevelEnabled: true,
              bevelThickness: 0.04,
              bevelSize: 0.02,
              bevelSegments: 2
            }
          ]}
        />
        <meshStandardMaterial
          color={ICE_COLORS.border}
          emissive={ICE_COLORS.border}
          emissiveIntensity={0.3}
          metalness={0.8}
          roughness={0.15}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Icicles hanging from top of border */}
      <Icicles width={columns * cellSize} />
    </group>
  );
};

export default IceBoardGrid3D;
